import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import Show from 'components/Show';
import Tooltip from 'components/Ui/Tooltip';
import { Button } from 'components/Misc/Form';
import { AverageMark, FinalMark } from 'components/Ui/Mark';
import { Title } from 'components/Misc/Typography';

import Add from 'containers/Administrator/Subjects/Profile/Studentz/Add';
import Update from 'containers/Administrator/Subjects/Profile/Studentz/Update';
import Course from 'containers/Administrator/Subjects/Profile/Studentz/Course';

const Grade = ({ grade, studentId }) => {
	// Cache current object's id
	const { id } = useParams();

	// Store modal's visibility
	const [addModal, setAddModal] = useState(false);
	const [updateModal, setUpdateModal] = useState(false);

	// Filter out marks by type
	const marks = useMemo(() => ({
		final: grade.marks.find(mark => mark.type === 'final'),
		middle: grade.marks.find(mark => mark.type === 'middle'),
		transferred: grade.marks.filter(mark => mark.type === 'transferred')
	}), [grade]);

	// Calculate avarage mark
	const average = useMemo(() => {
		const fromMarks = grade.marks.filter(mark => mark.type === 'transferred').map(mark => mark.mark);
		const fromCourses = grade.courses.reduce((result, course) => {
			if (course?.takes?.[0]?.mark) {
				return [...result, course?.takes?.[0]?.mark];
			}

			return result;
		}, []);
		const combined = [...fromMarks, ...fromCourses];
		const sum = combined.reduce((a, b) => a + Number(b), 0);
		const avg = Number(sum / combined.length).toFixed(1);
		return isNaN(avg) ? 'nv' : avg;
	}, [grade]);

	// Toggle modal's viAddsibility
	const handleAddModal = () => setAddModal(m => !m);
	const handleUpdateModal = () => setUpdateModal(m => !m);

	return (
		<div className="flex-1 border border-neutral-100 dark:border-neutral-700 rounded-lg">
			<div className="flex border-b border-neutral-100 dark:border-neutral-700">
				<div className="py-1 px-2 flex-1">
					<Title title={grade.title} />
				</div>

				<div className="py-1 px-2 w-11 text-center">
					<Title title="V" />
				</div>

				<div className="py-1 px-2 w-11 text-center">
					<Title title="G" />
				</div>
			</div>

			<div className="flex">
				{/* Course marks */}
				<div className="p-2 flex-1 flex flex-wrap gap-2">
					{/* Transferred marks */}
					{marks.transferred?.map(mark =>
						<Tooltip key={mark.id} content={() =>
							<div className="py-1 px-2 bg-white text-neutral-700 whitespace-nowrap rounded-lg">
								<div className="text-sm font-bold text-neutral-700">
									Pārnest vērtējums
								</div>
							</div>
						}>
							<FinalMark mark={mark} />
						</Tooltip>
					)}

					{/* Course marks */}
					{grade.courses.map(course =>
						<Course key={course.id} course={course} />
					)}
				</div>

				<div className="p-2">
					<AverageMark mark={average} />
				</div>

				<div className="p-2">
					{/* Middle mark */}
					<Show if={marks.middle}>
						<Tooltip content={() =>
							<div className="py-1 px-2 bg-white text-neutral-700 whitespace-nowrap rounded-lg">
								<div className="text-sm font-bold text-neutral-700">
									Starpvērtējums
								</div>
							</div>
						}>
							<FinalMark mark={marks.middle} onClick={handleUpdateModal} />
						</Tooltip>
					</Show>

					{/* Final mark */}
					<Show if={marks.final}>
						<Tooltip content={() =>
							<div className="py-1 px-2 bg-white text-neutral-700 whitespace-nowrap rounded-lg">
								<div className="text-sm font-bold text-neutral-700">
									Gala vērtējums
								</div>
							</div>
						}>
							<FinalMark mark={marks.final} onClick={handleUpdateModal} />
						</Tooltip>
					</Show>

					{/* Add mark */}
					<Show if={!marks.final && !marks.middle}>
						<Button icon="add" square small onClick={handleAddModal} />
					</Show>
				</div>
			</div>
			
			{/* Mark addition */}
			<Add
				studentId={studentId}
				subjectId={id}
				gradeId={grade.id}
				modal={addModal}
				handleModal={handleAddModal}
			/>

			{/* Existing mark update */}
			<Update
				studentId={studentId}
				subjectId={id}
				gradeId={grade.id}
				mark={marks.middle ?? marks.final}
				modal={updateModal}
				handleModal={handleUpdateModal}
			/>
		</div>
	);
};

export default Grade;